/**
 * The ONE shell for embedded roster/member PREVIEWS — the capped tables
 * inside detail pages (a client's roster, a cohort's members).
 *
 * Two of these had drifted apart by 2026-08-06: different paddings,
 * different header styling, one linking the student's name and the other
 * carrying a trailing "Edit" column (which the conventions ban — the row
 * is the link). The shell owns the table chrome; each context keeps its
 * own columns beyond the shared base, because a cohort answers questions
 * a client does not (who overrides the shared date, who mismatches the
 * classification default).
 *
 * Rows stay in the page: use `TABLE_ROW_NESTED` for the row and
 * `PREVIEW_CELL` for cells, and link the student's NAME to the edit page
 * (`PreviewNameLink`) rather than adding an action column for
 * navigation. An action column is for genuine actions (Remove).
 *
 * Previews stay previews: capped, with the total and a link to the paged
 * list — see the conventions doc §Page shape.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/** Cell padding, shared by header and body so columns cannot misalign. */
export const PREVIEW_CELL = 'p-2'

export function PreviewTable({
  headers,
  hasActionColumn = false,
  children,
}: {
  /** Column titles, in order. The base is Name / Email / Tier; a context
   *  appends its own after those. */
  headers: string[]
  /** True when rows end in an action cell (Remove, …) — never navigation. */
  hasActionColumn?: boolean
  children: ReactNode
}): JSX.Element {
  return (
    <table className="w-full text-table">
      <thead>
        <tr className="text-left text-text-secondary border-b border-border-default">
          {headers.map((header) => (
            <th key={header} className={`${PREVIEW_CELL} font-medium`}>
              {header}
            </th>
          ))}
          {hasActionColumn && (
            <th className={PREVIEW_CELL}>
              <span className="sr-only">Actions</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

/** The student's name IS the link to their edit page — no trailing
 *  Edit/Manage column. */
export function PreviewNameLink({
  userId,
  children,
}: {
  userId: string
  children: ReactNode
}): JSX.Element {
  return (
    <Link to={`/admin/students/${userId}/edit`} className="underline">
      {children}
    </Link>
  )
}
